import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Loader2, Rocket, AlertTriangle } from 'lucide-react';

interface Release {
  id: string;
  name: string;
  status: string;
  target_date: string | null;
}

interface ReleaseTask {
  external_id: string;
  status: string;
  release_id: string | null;
}

interface Dependency {
  source_issue_external_id: string;
  target_issue_external_id: string;
  link_type: string;
}

interface ReleaseSummary {
  release: Release;
  totalIssues: number;
  doneIssues: number;
  completionPct: number;
  blockerCount: number;
  health: 'green' | 'amber' | 'red';
}

const HEALTH_LABEL: Record<string, { label: string; className: string }> = {
  green: { label: 'On Track', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  amber: { label: 'At Risk', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  red: { label: 'Off Track', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

const DONE_STATUSES = new Set(['done']);

const ReleaseIntelligenceView = () => {
  const { tenantId } = useAuth();
  const [summaries, setSummaries] = useState<ReleaseSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);

    const releasesQuery = supabase.from('releases').select('id, name, status, target_date');
    if (tenantId) releasesQuery.eq('tenant_id', tenantId);
    const { data: releases } = await releasesQuery;

    const tasksQuery = supabase.from('tasks_sync').select('external_id, status, release_id').not('release_id', 'is', null);
    if (tenantId) tasksQuery.eq('tenant_id', tenantId);
    const { data: tasks } = await tasksQuery;

    const depsQuery = supabase.from('issue_dependencies').select('source_issue_external_id, target_issue_external_id, link_type').eq('link_type', 'blocked_by');
    if (tenantId) depsQuery.eq('tenant_id', tenantId);
    const { data: dependencies } = await depsQuery;

    const releaseList = (releases ?? []) as Release[];
    const taskList = (tasks ?? []) as ReleaseTask[];
    const depList = (dependencies ?? []) as Dependency[];
    const tasksByExternalId = new Map(taskList.map((t) => [t.external_id, t]));

    const computed = releaseList.map((release) => {
      const releaseTasks = taskList.filter((t) => t.release_id === release.id);
      const totalIssues = releaseTasks.length;
      const doneIssues = releaseTasks.filter((t) => DONE_STATUSES.has(t.status)).length;
      const completionPct = totalIssues > 0 ? Math.round((doneIssues / totalIssues) * 100) : 0;

      const releaseTaskIds = new Set(releaseTasks.map((t) => t.external_id));
      const blockerCount = depList.filter((d) => {
        if (!releaseTaskIds.has(d.source_issue_external_id)) return false;
        const blockedTask = tasksByExternalId.get(d.source_issue_external_id);
        const blockerTask = tasksByExternalId.get(d.target_issue_external_id);
        if (!blockedTask || DONE_STATUSES.has(blockedTask.status)) return false;
        if (blockerTask && DONE_STATUSES.has(blockerTask.status)) return false;
        return true;
      }).length;

      let health: ReleaseSummary['health'] = 'green';
      if (blockerCount > 0) health = 'red';
      else if (totalIssues > 0 && completionPct < 70) health = 'amber';

      return { release, totalIssues, doneIssues, completionPct, blockerCount, health };
    });

    setSummaries(computed);
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Rocket className="h-5 w-5 text-primary" />
          Dependencies
        </h2>
        <p className="text-sm text-muted-foreground mt-1">Health, completion, and blockers for every tracked release.</p>
      </div>

      {summaries.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No releases synced yet — connect Jira and sync issues with a fix version set.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {summaries.map((s) => (
            <Card key={s.release.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{s.release.name}</CardTitle>
                  <Badge className={HEALTH_LABEL[s.health].className}>{HEALTH_LABEL[s.health].label}</Badge>
                </div>
                {s.release.target_date && (
                  <CardDescription>Target: {new Date(s.release.target_date).toLocaleDateString()}</CardDescription>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Completion</span>
                    <span className="font-medium text-foreground">{s.completionPct}%</span>
                  </div>
                  <Progress value={s.completionPct} className="h-2" />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Issues</span>
                  <span className="font-medium text-foreground">{s.doneIssues} / {s.totalIssues} done</span>
                </div>
                {s.blockerCount > 0 && (
                  <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                    <AlertTriangle className="h-4 w-4" />
                    <span>{s.blockerCount} blocked issue{s.blockerCount > 1 ? 's' : ''}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReleaseIntelligenceView;
